import React, { useCallback, useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import ChallengeCard from "../components/ChallengeCard";
import ChallengeDetail from "../components/ChallengeDetail";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Lightning } from "@phosphor-icons/react";
import { generateChallenges, getContext, generateMarketDecisionChallenge } from "../lib/api";

const TYPES = [
    "PICK", "PREDICTION", "VERSUS", "SCENARIO", "SQUAD_BUILDING",
    "MARKET", "HIDDEN_GEM_HUNT", "HISTORICAL_MATCHUP",
];

const SKILL_OPTIONS = [
    { value: "", label: "Any skill" },
    { value: "skill_analytical", label: "Analytical" },
    { value: "skill_strategicplanning", label: "Strategic Planning" },
    { value: "skill_adaptability", label: "Adaptability" },
    { value: "skill_premierleagueknowledge", label: "PL Knowledge" },
    { value: "skill_riskmanagement", label: "Risk Management" },
    { value: "skill_decisionmakingunderpressure", label: "Decision Under Pressure" },
    { value: "skill_financialmanagement", label: "Financial Management" },
    { value: "skill_longtermvision", label: "Long-term Vision" },
];

export default function Generator() {
    const [context, setContext] = useState(null);

    const [count, setCount] = useState(3);
    const [genWindow, setGenWindow] = useState(3);
    const [questionsPerChallenge, setQuestionsPerChallenge] = useState(2);
    const [skill, setSkill] = useState("");
    const [allowed, setAllowed] = useState(TYPES);
    const [generating, setGenerating] = useState(false);
    const [humanPrompt, setHumanPrompt] = useState("");
    const [language, setLanguage] = useState("EN");
    const [skipUnavailable, setSkipUnavailable] = useState(false);
    const [newManagerSignal, setNewManagerSignal] = useState(false);

    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);

    // Market Decision (Transfer Desk) one-click state
    const [mdMode, setMdMode] = useState("MARKET_CALL");
    const [mdCount, setMdCount] = useState(10);
    const [mdSegments, setMdSegments] = useState(["market", "hidden_gems", "traps"]);
    const [mdGenerating, setMdGenerating] = useState(false);

    const toggleSegment = (s) => {
        setMdSegments((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
    };

    const runMarketDecision = async () => {
        if (!mdSegments.length) {
            toast.error("Pick at least one segment.");
            return;
        }
        setMdGenerating(true);
        try {
            const ch = await generateMarketDecisionChallenge({
                mode: mdMode,
                count: mdCount,
                segments: mdSegments,
                duration_window: genWindow,
            });
            toast.success(`Transfer Desk ready · ${ch.players?.length || 0} players · GW${ch.duration?.fromGameweek}–${ch.duration?.toGameweek}`);
            window.open(`/play/market/${ch.id}`, "_blank");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Market challenge generation failed");
        } finally {
            setMdGenerating(false);
        }
    };

    const loadContext = useCallback(async () => {
        try {
            const ctx = await getContext(genWindow);
            setContext(ctx);
        } catch (err) {
            // non-blocking — generator works without sidebar context
            console.warn("Generator: live-context fetch failed", err);
        }
    }, [genWindow]);

    useEffect(() => { loadContext(); }, [loadContext]);

    const toggleType = (t) => {
        setAllowed((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
    };

    const run = async () => {
        setGenerating(true);
        setResults([]);
        try {
            const res = await generateChallenges({
                count,
                gameweekWindow: genWindow,
                questionsPerChallenge,
                skill: skill || null,
                language,
                allowedTypes: allowed,
                humanPrompt: humanPrompt.trim() || null,
                skipUnavailable,
                newManagerSignal,
            });
            setResults(res.challenges);
            const avg = res.challenges.length
                ? Math.round(
                    res.challenges.reduce((s, c) => s + c.qualityScore, 0)
                    / res.challenges.length,
                ) : 0;
            toast.success(`${res.challenges.length} challenges generated · avg quality ${avg}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Generation failed");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <AppShell liveContext={context}>
            <div className="space-y-8" data-testid="generator-page">
                {/* Hero */}
                <section className="relative border border-white/10 bg-[#0a0a0a] overflow-hidden">
                    <div className="grain-overlay" />
                    <div
                        className="absolute inset-0 opacity-20"
                        style={{
                            backgroundImage:
                                "url(https://images.pexels.com/photos/14963646/pexels-photo-14963646.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter: "grayscale(100%) brightness(0.4)",
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
                    <div className="relative p-6 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
                        <div className="md:col-span-7 space-y-4">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                                <Lightning size={12} weight="fill" />
                                Gameweek {context?.gameweek || "—"} · Generator window: next {genWindow} GW
                            </div>
                            <h1 className="font-display font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                                Challenge
                                <br />
                                <span className="text-fa-yellow">Generator.</span>
                            </h1>
                            <p className="text-white/70 max-w-xl leading-relaxed">
                                Real FPL data, Premier League context and Fantasy Academy tone — fused into
                                publication-ready challenges scored out of 100.
                            </p>
                            <p className="font-mono text-[11px] text-white/50">
                                Looking for race tables, threat data and bandwagons? See the{" "}
                                <a href="/" className="text-fa-cream underline-offset-2 hover:underline">
                                    Dashboard
                                </a>.
                            </p>
                        </div>

                        {/* Control panel */}
                        <div className="md:col-span-5 bg-[#121212] border border-white/10 p-5 space-y-4" data-testid="generator-control">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                                Generator Control
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <NumberField label="Count" value={count} onChange={setCount} min={1} max={10} testid="input-count" />
                                <NumberField label="GW Window" value={genWindow} onChange={setGenWindow} min={1} max={10} testid="input-gen-window" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <NumberField label="Questions / Ch." value={questionsPerChallenge} onChange={setQuestionsPerChallenge} min={1} max={5} testid="input-questions" />
                                <div>
                                    <Label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                                        Skill focus
                                    </Label>
                                    <select
                                        value={skill}
                                        onChange={(e) => setSkill(e.target.value)}
                                        className="w-full bg-black border border-white/10 rounded-none font-mono text-xs px-2 py-2 text-white focus:outline-none focus:border-fa-yellow h-10"
                                        data-testid="select-skill"
                                    >
                                        {SKILL_OPTIONS.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <Label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-2 block">
                                    Allowed types
                                </Label>
                                <div className="grid grid-cols-2 gap-2">
                                    {TYPES.map((t) => (
                                        <label
                                            key={t}
                                            className={`flex items-center gap-2 px-2 py-1.5 border cursor-pointer ${
                                                allowed.includes(t)
                                                    ? "border-fa-yellow/60 bg-fa-yellow/5"
                                                    : "border-white/10 hover:border-white/30"
                                            }`}
                                            data-testid={`type-toggle-${t}`}
                                        >
                                            <Checkbox
                                                checked={allowed.includes(t)}
                                                onCheckedChange={() => toggleType(t)}
                                                className="rounded-none border-white/30 data-[state=checked]:bg-fa-yellow data-[state=checked]:text-black"
                                            />
                                            <span className="font-mono text-[10px] uppercase tracking-wider">
                                                {t.replace("_", " ")}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <Label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block flex items-center gap-2">
                                    Moderator prompt
                                    <span className="text-white/30 normal-case tracking-normal">
                                        (optional — steers the LLM narrative)
                                    </span>
                                </Label>
                                <Textarea
                                    value={humanPrompt}
                                    onChange={(e) => setHumanPrompt(e.target.value)}
                                    rows={3}
                                    placeholder="e.g. Focus on hidden-gem midfielders with good historical records vs Arsenal."
                                    className="bg-black border-white/10 rounded-none font-mono text-xs resize-y"
                                    data-testid="human-prompt-textarea"
                                />
                            </div>

                            {/* Phase F output controls */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                    <Label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                                        Language
                                    </Label>
                                    <div className="flex gap-1" data-testid="lang-toggle">
                                        {["EN", "CS"].map((code) => (
                                            <button
                                                key={code}
                                                type="button"
                                                onClick={() => setLanguage(code)}
                                                data-testid={`lang-${code}`}
                                                className={`flex-1 font-mono text-xs uppercase tracking-widest h-9 border transition-colors ${language === code
                                                    ? "bg-fa-cream text-black border-fa-cream"
                                                    : "border-white/20 hover:border-fa-cream/50 text-white/70"
                                                    }`}
                                            >
                                                {code === "EN" ? "EN" : "CS"}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <ToggleCard
                                    label="Skip unavailable"
                                    hint="Excludes doubtful + COP < 75"
                                    checked={skipUnavailable}
                                    onChange={setSkipUnavailable}
                                    testid="skip-unavailable-toggle"
                                />
                                <ToggleCard
                                    label="New manager tone"
                                    hint="Softer, explains FPL jargon"
                                    checked={newManagerSignal}
                                    onChange={setNewManagerSignal}
                                    testid="new-manager-toggle"
                                />
                            </div>

                            <Button
                                onClick={run}
                                disabled={generating || !allowed.length}
                                className="w-full rounded-none bg-fa-yellow hover:bg-[#CBE815] text-black uppercase font-black font-display tracking-wide text-lg py-6"
                                data-testid="generate-btn"
                            >
                                {generating ? (
                                    <span className="flex items-center gap-2">
                                        <span className="inline-block w-2 h-2 bg-black animate-pulse" />
                                        Generating…
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-2">
                                        <Lightning size={18} weight="fill" />
                                        Run Generator
                                    </span>
                                )}
                            </Button>
                        </div>
                    </div>
                </section>

                {/* Transfer Desk one-click generator */}
                <section className="border border-fa-violet/30 bg-[#0d0d0d] p-5 space-y-4" data-testid="transfer-desk-panel">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-yellow flex items-center gap-2">
                                <Lightning size={11} weight="fill" />
                                Transfer Desk · Market Decision Game
                            </div>
                            <h2 className="font-display font-black text-2xl uppercase tracking-tight mt-1">
                                One-click challenge
                            </h2>
                            <p className="font-mono text-[11px] text-white/50 mt-1">
                                Generates a 10-player BUY / KEEP / AVOID / SELL gauntlet from the live market, hidden gems and trap signals. Graded automatically after the GW.
                            </p>
                        </div>
                        <Button
                            onClick={runMarketDecision}
                            disabled={mdGenerating || !mdSegments.length}
                            className="rounded-none bg-fa-violet hover:bg-fa-violetHover text-white uppercase font-black font-display tracking-wide px-6 py-5"
                            data-testid="md-generate-btn"
                        >
                            {mdGenerating ? "Generating…" : "Generate Transfer Desk →"}
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* Mode toggle */}
                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                                Mode
                            </Label>
                            <div className="flex gap-1" data-testid="md-mode-toggle">
                                {[
                                    { code: "MARKET_CALL", label: "Market Call", hint: "Free pick" },
                                    { code: "TRANSFER_DESK", label: "Transfer Desk", hint: "Even split" },
                                ].map(({ code, label, hint }) => (
                                    <button
                                        key={code}
                                        type="button"
                                        onClick={() => setMdMode(code)}
                                        data-testid={`md-mode-${code}`}
                                        title={hint}
                                        className={`flex-1 font-mono text-[10px] uppercase tracking-widest h-9 px-2 border transition-colors ${
                                            mdMode === code
                                                ? "bg-fa-violet text-white border-fa-violet"
                                                : "border-white/20 hover:border-fa-violet/50 text-white/70"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Count */}
                        <NumberField
                            label="Players (5–20)"
                            value={mdCount}
                            onChange={(v) => setMdCount(Math.max(5, Math.min(20, v)))}
                            min={5}
                            max={20}
                            testid="md-count"
                        />

                        {/* Segments */}
                        <div>
                            <Label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                                Segments
                            </Label>
                            <div className="flex flex-wrap gap-1.5" data-testid="md-segment-toggle">
                                {[
                                    { id: "market", label: "Market" },
                                    { id: "hidden_gems", label: "Hidden Gems" },
                                    { id: "traps", label: "Traps" },
                                ].map(({ id, label }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => toggleSegment(id)}
                                        data-testid={`md-segment-${id}`}
                                        className={`font-mono text-[10px] uppercase tracking-widest h-9 px-3 border transition-colors ${
                                            mdSegments.includes(id)
                                                ? "bg-fa-cream text-black border-fa-cream"
                                                : "border-white/20 hover:border-fa-cream/50 text-white/70"
                                        }`}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Results */}
                {results.length > 0 && (
                    <section>
                        <div className="flex items-end justify-between mb-4">
                            <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight">
                                Generated Challenges
                            </h2>
                            <div className="flex-1 h-px bg-white/10 ml-4" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="results-grid">
                            {results.map((c, i) => (
                                <ChallengeCard key={c.id} challenge={c} onClick={setSelected} index={i} />
                            ))}
                        </div>
                    </section>
                )}
            </div>

            <ChallengeDetail
                challenge={selected}
                open={!!selected}
                onOpenChange={(v) => !v && setSelected(null)}
                onChanged={async () => {
                    if (selected) {
                        const fresh = await fetch(
                            `${process.env.REACT_APP_BACKEND_URL}/api/challenges/${selected.id}`,
                        ).then((r) => r.json());
                        setSelected(fresh);
                        setResults((prev) => prev.map((c) => (c.id === fresh.id ? fresh : c)));
                    }
                }}
            />
        </AppShell>
    );
}

function NumberField({ label, value, onChange, min, max, testid }) {
    return (
        <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest text-white/60 mb-1 block">
                {label}
            </Label>
            <Input
                type="number"
                min={min}
                max={max}
                value={value}
                onChange={(e) => onChange(Number(e.target.value) || min)}
                className="bg-black border-white/10 rounded-none font-mono"
                data-testid={testid}
            />
        </div>
    );
}

function ToggleCard({ label, hint, checked, onChange, testid }) {
    return (
        <label
            className={`flex flex-col gap-1 px-3 py-2 border cursor-pointer transition-colors ${checked
                ? "border-fa-cream/60 bg-fa-cream/10"
                : "border-white/15 hover:border-fa-cream/30"
                }`}
            data-testid={testid}
        >
            <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest text-white/70">
                    {label}
                </span>
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => onChange(e.target.checked)}
                    className="w-3.5 h-3.5 accent-fa-cream"
                />
            </div>
            <span className="font-mono text-[10px] text-white/40 leading-snug">{hint}</span>
        </label>
    );
}
